import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Info, Loader2, Settings2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router";
import { toast } from "sonner";

import { APIError } from "@/adapters/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AddressFields } from "@/features/canteiros/components/address-fields";
import { useUpdateCanteiro } from "@/features/canteiros/hooks/use-update-canteiro.ts";
import { useWorksite } from "@/features/canteiros/hooks/use-worksite.ts";
import {
	type CanteiroFormValues,
	canteiroSchema,
} from "@/features/canteiros/schemas/canteiro-schema.ts";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";

const VIOLATION_FIELDS: Record<string, keyof CanteiroFormValues> = {
	"address.street": "street",
	"address.number": "number",
	"address.complement": "complement",
	"address.neighborhood": "neighborhood",
	"address.city": "city",
	"address.state": "state",
	"address.zipcode": "zipcode",
};

export function EnderecoObraPage() {
	const { obra: obraKey } = useParams();
	const {
		obra,
		obras,
		isLoading: obraLoading,
		isError: obraError,
		refetch: refetchObra,
	} = useActiveObra();
	const canteiroId = obra?.canteiroId;
	const {
		data: worksite,
		isLoading: worksiteLoading,
		isError: worksiteError,
	} = useWorksite(canteiroId);
	const updateCanteiro = useUpdateCanteiro(canteiroId);
	const [formError, setFormError] = useState<string | null>(null);

	const shared = obras.filter((o) => o.canteiroId === canteiroId).length > 1;

	const {
		register,
		handleSubmit,
		setError,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<CanteiroFormValues>({
		resolver: zodResolver(canteiroSchema),
		defaultValues: {
			name: "",
			street: "",
			number: "",
			complement: "",
			neighborhood: "",
			city: "",
			state: "",
			zipcode: "",
			lat: undefined,
			lng: undefined,
			area: "",
			startDate: "",
			expectedEndDate: "",
		},
	});

	useEffect(() => {
		if (!worksite) return;
		reset({
			name: worksite.name,
			street: worksite.address.street,
			number: worksite.address.number,
			complement: worksite.address.complement ?? "",
			neighborhood: worksite.address.neighborhood,
			city: worksite.address.city,
			state: worksite.address.state,
			zipcode: worksite.address.zipcode,
			lat: worksite.address.lat,
			lng: worksite.address.lng,
			area: "",
			startDate: "",
			expectedEndDate: "",
		});
	}, [worksite, reset]);

	function applyAPIError(err: unknown, fallback: string) {
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, message] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, { message });
					matchedField = true;
				}
			}
			if (!matchedField) setFormError(err.message || fallback);
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: CanteiroFormValues) {
		setFormError(null);
		try {
			await updateCanteiro.mutateAsync({
				address: {
					street: values.street,
					number: values.number,
					complement: values.complement?.trim() || undefined,
					neighborhood: values.neighborhood,
					city: values.city,
					state: values.state.toUpperCase(),
					zipcode: values.zipcode,
					lat: values.lat,
					lng: values.lng,
				},
			});
			toast.success("Endereço atualizado com sucesso!");
		} catch (err) {
			applyAPIError(err, "Não foi possível atualizar o endereço.");
		}
	}

	const isLoading = obraLoading || (!!canteiroId && worksiteLoading);

	if (isLoading) {
		return (
			<div className="page-loading">
				<Loader2 />
			</div>
		);
	}

	if (obraError || worksiteError || !worksite) {
		return (
			<Card>
				<CardContent className="card-state">
					<p>Não foi possível carregar o endereço.</p>
					<Button variant="outline" onClick={() => refetchObra()}>
						Tentar novamente
					</Button>
				</CardContent>
			</Card>
		);
	}

	const isPending = updateCanteiro.isPending;

	return (
		<div className="endereco-obra-page">
			<Link
				to={`/${obraKey}/configuracoes-da-obra`}
				className="endereco-obra-page__back"
			>
				<ArrowLeft />
				Configurações da obra
			</Link>
			<div className="page-header">
				<div>
					<h1 className="page-title">
						{shared ? "Endereço do canteiro" : "Endereço da obra"}
					</h1>
					<p className="page-subtitle">
						{shared ? "Onde fica o canteiro desta obra." : "Onde fica a obra."}
					</p>
				</div>
				{shared && canteiroId && (
					<div className="page-header__actions">
						<Button asChild variant="outline" size="sm">
							<Link to={`/editar-canteiro/${canteiroId}`}>
								<Settings2 />
								Gerenciar canteiro
							</Link>
						</Button>
					</div>
				)}
			</div>

			{shared && (
				<div className="endereco-obra-page__shared-note">
					<Info />
					<p>Este endereço vale para todas as obras deste canteiro.</p>
				</div>
			)}

			<form
				className="novo-canteiro-page__form"
				onSubmit={handleSubmit(onSubmit)}
				noValidate
			>
				<AddressFields
					register={register}
					errors={errors}
					setValue={setValue}
					watch={watch}
					idPrefix="endereco"
				/>

				{formError && (
					<p role="alert" className="form-error-alert">
						{formError}
					</p>
				)}

				<div className="novo-canteiro-page__actions">
					<Button type="submit" disabled={isPending}>
						{isPending && <Loader2 className="novo-canteiro-page__spinner" />}
						Salvar alterações
					</Button>
				</div>
			</form>
		</div>
	);
}
